import {FormControl, FormField, IconButton, OptionType, Radio} from 'platform/components';
import {Box, Heading, HStack, Link, Show, Space, VStack} from 'platform/foundation';

import {useFieldArray, UseFormReturn, useWatch} from 'react-hook-form';

import i18n from '@dms/i18n';
import {useWorkTypeOptions} from '@dms/shared';

import {CurrencyCodeType, suffixTestId, TestIdProps} from 'shared/web';

import {PRICING_FIELD_WIDTH} from '../constants/PRICING_FIELD_WIDTH';
import {DiscountSectionFormType} from '../schemas/discountSectionSchema';
import {WorkDiscountOption} from '../utils/workDiscountOption';

const WORK_OPTIONS = [
  {
    label: i18n.t('entity.order.labels.percentageDiscount'),
    value: WorkDiscountOption.PercentageDiscount,
  },
  {
    label: i18n.t('entity.order.labels.discountedWorkRate'),
    value: WorkDiscountOption.DiscountedWorkRate,
  },
];

interface DiscountOnWorkProps extends TestIdProps {
  control: FormControl<DiscountSectionFormType>;
  formApi: UseFormReturn<DiscountSectionFormType>;
  currency?: CurrencyCodeType;
  isReadOnly?: boolean;
}

export function DiscountOnWork(props: DiscountOnWorkProps) {
  const {workTypeOptions, isLoading: isOptionsLoading} = useWorkTypeOptions();
  const {fields, append, remove} = useFieldArray({
    control: props.control,
    name: 'work.discountedWorkRates.discountedWorkRate',
  });

  const isExpanded = useWatch({control: props.control, name: 'isWorkExpanded'});
  const selectedOption = useWatch({control: props.control, name: 'selectedWorkOption'});
  const discountedWorkRates = useWatch({
    control: props.control,
    name: 'work.discountedWorkRates.discountedWorkRate',
  });

  const appendEmptyFields = () =>
    append({workRatePriceWithoutVat: undefined, workRateType: undefined});

  const shouldOptionBeDisplayed = (option: OptionType) =>
    !discountedWorkRates?.map((field) => field.workRateType).includes(option.value);

  return (
    <VStack spacing={4}>
      <HStack align="center" spacing={3}>
        <FormField
          control={props.control}
          name="isWorkExpanded"
          type="switch"
          isDisabled={props.isReadOnly}
          data-testid={suffixTestId('expand', props)}
        />
        <Heading size={4}>{i18n.t('entity.order.labels.discountOnWork')}</Heading>
      </HStack>
      <Show when={isExpanded}>
        <VStack spacing={4}>
          <Radio
            options={WORK_OPTIONS}
            value={selectedOption}
            onChange={(value) =>
              props.formApi.setValue('selectedWorkOption', value as WorkDiscountOption, {
                shouldDirty: true,
              })
            }
            isDisabled={props.isReadOnly}
            data-testid={suffixTestId('type', props)}
          />
          <Show when={selectedOption === WorkDiscountOption.PercentageDiscount}>
            <Box width={PRICING_FIELD_WIDTH} maxWidth="100%">
              <FormField
                control={props.control}
                name="work.percentDiscount.discountRate"
                type="number"
                label={i18n.t('entity.order.labels.percentage')}
                isDisabled={props.isReadOnly}
                suffix="%"
                decimalPlaces={2}
                data-testid={suffixTestId('discountRate', props)}
              />
            </Box>
          </Show>
          <Show when={selectedOption === WorkDiscountOption.DiscountedWorkRate}>
            {fields.map((field, i) => {
              const isFirst = i === 0;
              const removeFields = () => remove(i);
              return (
                <HStack key={field.id} spacing={4}>
                  <Box width={PRICING_FIELD_WIDTH} minWidth={0}>
                    <FormField
                      isLoading={isOptionsLoading}
                      control={props.control}
                      name={`work.discountedWorkRates.discountedWorkRate.${i}.workRateType`}
                      type="choice"
                      options={workTypeOptions}
                      filterOption={shouldOptionBeDisplayed}
                      label={isFirst ? i18n.t('entity.order.labels.workType') : null}
                      menuInPortal
                      isDisabled={props.isReadOnly}
                      data-testid={suffixTestId(`workType-[${i}]`, props)}
                    />
                  </Box>
                  <Box width={PRICING_FIELD_WIDTH} minWidth={0}>
                    <FormField
                      control={props.control}
                      name={`work.discountedWorkRates.discountedWorkRate.${i}.workRatePriceWithoutVat`}
                      type="currency"
                      currency={props.currency}
                      label={isFirst ? i18n.t('entity.order.labels.workRateWithoutVAT') : null}
                      isDisabled={props.isReadOnly}
                      data-testid={suffixTestId(`workRate-[${i}]`, props)}
                    />
                  </Box>
                  {/* Keeps the field widths the same whether the remove buttons show or not. */}
                  <Box width={8} flexShrink={0}>
                    <Show when={fields.length > 1}>
                      <Show when={isFirst}>
                        <Space vertical={5} />
                      </Show>
                      <IconButton
                        icon="navigation/close"
                        severity="danger"
                        priority="tertiary"
                        isDisabled={props.isReadOnly}
                        onClick={removeFields}
                        data-testid={suffixTestId(`removeField-[${i}]`, props)}
                      />
                    </Show>
                  </Box>
                </HStack>
              );
            })}
            <Link
              onClick={appendEmptyFields}
              size="small"
              title={i18n.t('entity.order.actions.addWorkType')}
              isDisabled={props.isReadOnly}
              data-testid={suffixTestId('addWorkType', props)}
            />
          </Show>
        </VStack>
      </Show>
    </VStack>
  );
}
